import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toast } from "@shared/ui/use-toast";
import { meterApi, mxDeviceManagementApi } from "./api";
import { useCallback, useState } from "react";
import { downloadCSV } from "@shared/utils/utils";

export const useInventory = ({
  remoteUtilityId,
  tab,
  page,
  limit,
  search,
  meter_type,
  manufacturer,
}: {
  remoteUtilityId: string;
  tab: string;
  page: number;
  limit: number;
  search?: string;
  meter_type?: string;
  manufacturer?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.inventory(
      tab,
      page,
      limit,
      search,
      meter_type,
      manufacturer,
    ),
    () =>
      mxDeviceManagementApi.getInventory({
        remoteUtilityId,
        tab,
        page,
        limit,
        search,
        meter_type,
        manufacturer,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );
};

export const useInventoryExport = (
  remoteUtilityId: string,
  tab: string,
  search?: string,
  meter_type?: any,
  manufacturer?: any,
) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const blob = await mxDeviceManagementApi.exportInventory({
        remoteUtilityId,
        tab,
        search,
        meter_type,
        manufacturer,
      });

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export inventory: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [
    remoteUtilityId,
    tab,
    search,
    meter_type,
    manufacturer,
    isExporting,
    toast,
  ]);

  return { exportCSV, isExporting };
};

export const useGetPremise = (code: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.premises(code),
    () => mxDeviceManagementApi.getPremises(utilityId, code),
    {
      enabled: !!code?.length && !!utilityId,
    },
  );
};

export const useGetSubArea = (codeList: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.subareas(codeList),
    () => mxDeviceManagementApi.getSubArea(codeList, utilityId),
    {
      enabled: !!codeList && !!utilityId,
    },
  );
};

export const useChoices = ({
  choice,
  remoteUtilityId,
}: {
  choice: string;
  remoteUtilityId?: string;
}) => {
  return useSmartQuery(QueryKeyFactory.module.mx.choices(choice), () =>
    mxDeviceManagementApi.getMxChoices(choice, remoteUtilityId),
  );
};
export const usePriorities = ({
  choice,
}: {
  choice: string;
  remoteUtilityId?: string;
}) => {
  return useSmartQuery(QueryKeyFactory.module.mx.choices(choice), () =>
    mxDeviceManagementApi.getPriorities(choice),
  );
};

export const useServiceOrdersList = ({
  remoteUtilityId,
  page,
  limit,
  status,
  searchData,
  created_date_gte,
  created_date_lte,
  operation_type,
}: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  status?: string | null;
  searchData?: string;
  created_date_gte?: string | null;
  created_date_lte?: string | null;
  operation_type?: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.serviceOrders(
      page,
      status,
      searchData,
      created_date_gte,
      created_date_lte,
      operation_type,
    ),
    () =>
      mxDeviceManagementApi.getServiceOrders({
        remoteUtilityId,
        page,
        limit,
        status,
        searchData,
        created_date_gte,
        created_date_lte,
        operation_type,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: false,
    },
  );
};

export const useSearchSubmeters = (
  remoteUtilityId: string,
  search?: string,
  page?: number,
  limit?: number,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.searchSubmeters(
      remoteUtilityId,
      search,
      page,
      limit,
    ),
    () =>
      mxDeviceManagementApi.searchSubmeters({
        remoteUtilityId,
        search,
        page,
        limit,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: false,
    },
  );
};

export const useSearchMasterMeters = (
  remoteUtilityId: string,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.searchMasterMeters(remoteUtilityId, search),
    () =>
      mxDeviceManagementApi.searchMasterMeters({
        remoteUtilityId,
        search,
      }),
    {
      enabled: !!remoteUtilityId && search.length > 2,
      staleTime: 0,
      refetchOnMount: false,
    },
  );
};

export const useServiceOrdersJobCard = (
  remoteUtilityId: string,
  searchData?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.serviceOrdersJobCard(remoteUtilityId, searchData),
    () =>
      mxDeviceManagementApi.getServiceOrdersJobCard({
        remoteUtilityId,
        searchData,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: false,
    },
  );
};

export const useServiceOrdersExport = ({
  remoteUtilityId,
  status,
  searchData,
  created_date_gte,
  created_date_lte,
  operation_type,
}: {
  remoteUtilityId: string;
  status?: string | null;
  searchData?: string;
  created_date_gte?: string | null;
  created_date_lte?: string | null;
  operation_type?: number;
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const blob = await mxDeviceManagementApi.exportServiceOrders({
        remoteUtilityId,
        status,
        searchData,
        created_date_gte,
        created_date_lte,
        operation_type,
      });
      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export work orders: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [
    remoteUtilityId,
    status,
    searchData,
    created_date_gte,
    created_date_lte,
    operation_type,
    isExporting,
    toast,
  ]);

  return { exportCSV, isExporting };
};
export const useMeterModels = ({
  remoteUtilityId,
  isDistinctMeterMakes,
  meterMake,
  utilityService,
}: {
  remoteUtilityId: string;
  isDistinctMeterMakes?: boolean;
  meterMake?: string;
  utilityService?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meterModels(
      isDistinctMeterMakes,
      meterMake,
      utilityService,
    ),
    () =>
      mxDeviceManagementApi.getMeterModels({
        remoteUtilityId,
        isDistinctMeterMakes,
        meterMake,
        utilityService,
      }),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const usePostBulkMeter = () => {
  return useSmartMutation<any, Error, FormData>(
    mxDeviceManagementApi.postBulkMeter,
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Device added successfully.",
          variant: "default",
        });
        const key = QueryKeyFactory.module.mx.inventory(
          "in_stock",
          1,
          PAGE_SIZE,
          "",
          "",
          "",
        );

        queryClient.invalidateQueries({
          queryKey: key,
        });

        queryClient.refetchQueries({
          queryKey: key,
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useGetAllSOPs = ({
  remoteUtilityId,
  operationType,
}: {
  remoteUtilityId: any;
  operationType: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.sop(operationType),
    () => mxDeviceManagementApi.getAllSOPs({ remoteUtilityId, operationType }),
    {
      enabled: !!remoteUtilityId && !!operationType,
      refetchOnMount: true,
    },
  );
};

export const useBulkCreateWorkOrderJobCard = (payload?: any) => {
  return useSmartMutation(mxDeviceManagementApi.bulkCreateWorkOrderJobCard, {
    ...payload,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Service order raised successfully.",
        variant: "default",
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "service-orders"],
      });
    },
  });
};

export const useMeterSummary = (remoteUtilityId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meterSummary(),
    () => mxDeviceManagementApi.getMeterSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useDeviceManagementMeters = ({
  remoteUtilityId,
  page,
  limit,
  searchData,
  status,
  meterType,
}: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  searchData?: string;
  status?: string;
  meterType?: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.deviceMeters(
      page,
      limit,
      searchData,
      status,
      meterType,
    ),
    () =>
      mxDeviceManagementApi.getDeviceManagementMeters({
        remoteUtilityId,
        page,
        limit,
        searchData,
        status,
        meterType,
      }),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

// In your hooks file
export const useAllMeters = (params: {
  remoteUtilityId: string;
  page: number;
  limit: number;
  search?: string;
  status?: string;
  meter_type?: string;
  manufacturer?: string;
  model?: string;
  service?: string;
  area?: string;
  subarea?: string;
  premise?: string;
  reading_status?: string;
  dateFrom?: string;
  dateTo?: string;
  operationType?: number;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.useAllMeters(params), // ✅ Pass entire params object
    () => mxDeviceManagementApi.getAllMeters(params),
    {
      enabled: !!params.remoteUtilityId,
    },
  );
};
export const useDeviceManagementMetersExport = (
  remoteUtilityId: string,
  searchData?: string,
  status?: string,
  meterType?: any,
) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const blob = await mxDeviceManagementApi.exportDeviceManagementMeters({
        remoteUtilityId,
        searchData,
        status,
        meterType,
      });
      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export meters: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [remoteUtilityId, searchData, status, meterType, isExporting, toast]);

  return { exportCSV, isExporting };
};

export const useGenerateMeterNumber = (remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.generateMeterNumber(),
    () => mxDeviceManagementApi.generateMeterNumber(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useUtilityServices = (utilityId: string) => {
  return useSmartQuery(
    QueryKeyFactory.global.utility.services(),
    () => mxDeviceManagementApi.getUtilityServices(utilityId),
    {
      enabled: !!utilityId,
    },
  );
};

export const useConfigMapJson = (
  jsonType: "category_json" | "territory_json",
  remoteUtilityId: string,
  configLevel: string,
  codeLevel?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.configMapJson(jsonType, configLevel, codeLevel),
    () =>
      mxDeviceManagementApi.getConfigMapJson({
        jsonType,
        remoteUtilityId,
        configLevel,
        codeLevel,
      }),
    {
      enabled: !!jsonType && !!remoteUtilityId && !!configLevel,
    },
  );
};

export const useMeterSearchPrompt = ({
  remoteUtilityId,
  searchData,
  premise,
  productCode,
}: {
  remoteUtilityId: string;
  searchData: string;
  premise?: string;
  productCode?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meterSearchPrompt(
      premise,
      productCode,
      searchData,
    ),
    () =>
      mxDeviceManagementApi.getMeterSearchPrompt({
        remoteUtilityId,
        searchData,
        premise,
        productCode,
      }),
    {
      enabled: !!searchData, // enables fetch only when user has typed something
    },
  );
};

export const useCreateDevice = () => {
  return useSmartMutation(mxDeviceManagementApi.createDevice, {
    invalidateKeys: [
      QueryKeyFactory.module.mx.deviceMeters(1, PAGE_SIZE),
      QueryKeyFactory.module.mx.generateMeterNumber(),
    ],
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ["mx", "list"],
      });

      queryClient.refetchQueries({
        queryKey: ["mx", "create"],
      });
    },
  });
};

export const useConsumerProfile = (
  remoteUtilityId: string,
  consumerNo: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.consumerProfile(consumerNo),
    () =>
      mxDeviceManagementApi.getConsumerProfile({
        remoteUtilityId,
        consumerNo,
      }),
    {
      enabled: !!remoteUtilityId && !!consumerNo,
    },
  );
};

export const useMeterDetail = (
  meterId: number,
  remoteUtilityId: any,
  enabled = true,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meterDetail(meterId),
    () => mxDeviceManagementApi.getMeterDetail({ meterId, remoteUtilityId }),
    {
      enabled: !!meterId && !!remoteUtilityId && enabled,
    },
  );
};

export const useDeviceDetail = (deviceId: number, remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.detail(deviceId),
    () => mxDeviceManagementApi.getDeviceDetail({ deviceId, remoteUtilityId }),
    {
      enabled: !!deviceId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};
export const useDeviceReadings = ({
  meterId,
  searchData = "",
  page,
  pageSize,
  fromDate,
  toDate,
  status,
}: {
  meterId: any;
  searchData?: string;
  page: number;
  pageSize: number;
  fromDate?: string;
  toDate?: string;
  status?: string;
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.readings(
      meterId,
      searchData,
      page,
      pageSize,
      fromDate,
      toDate,
      status,
    ),
    () =>
      mxDeviceManagementApi.getDeviceReadings({
        meterId,
        searchData,
        page,
        pageSize,
        remoteUtilityId,
        fromDate,
        toDate,
        status,
      }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useConsumptionGraph = (meterId: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.consumption(meterId),
    () =>
      mxDeviceManagementApi.getConsumptionGraph({
        id: meterId,
        remoteUtilityId,
      }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      refetchOnWindowFocus: true,
    },
  );
};

export const useDeviceAllocationHistory = ({
  meterNumber,
  deviceNo,
}: {
  meterNumber: number;
  deviceNo: any;
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.deviceAllocationHistory(
      meterNumber,
      deviceNo,
    ),
    () =>
      mxDeviceManagementApi.getDeviceAllocationHistory({
        meter_number: meterNumber,
        device_no: deviceNo,
        remoteUtilityId,
      }),
    {
      enabled: !!meterNumber,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useAlerts = ({ meterId }: { meterId: number }) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(QueryKeyFactory.module.mx.device.alerts(meterId), () =>
    mxDeviceManagementApi.getAlerts({ meterId: meterId, remoteUtilityId }),
  );
};

export const useMeterWorkorderInfo = ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.meterWorkorderInfo(meterId),
    () =>
      mxDeviceManagementApi.getMeterWorkorderInfo({ meterId, remoteUtilityId }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useActivityLogs = ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.logs.activityLogs(meterId),
    () => mxDeviceManagementApi.getActivityLogs({ meterId, remoteUtilityId }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useMeterNotes = ({ meterId }: { meterId: number }) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.notes(),
    () => mxDeviceManagementApi.getMeterNotes({ meterId, remoteUtilityId }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};

export const usePostMeterNote = () => {
  const key = QueryKeyFactory.module.mx.device.notes();
  return useSmartMutation(
    (data: any) => mxDeviceManagementApi.postMeterNote(data),
    {
      onSuccess: () => {},
      invalidateKeys: [key],
    },
  );
};

export const usePostWorkOrderJobCard = () => {
  return useSmartMutation(mxDeviceManagementApi.postWorkOrderJobCard, {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Job card created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({
        queryKey: ["mx", "work-order"],
      });
    },
  });
};

export const usePatchDeviceManagement = (deviceId: any) =>
  useSmartMutation(mxDeviceManagementApi.patchMeterDetail, {
    invalidateKeys: [QueryKeyFactory.module.mx.deviceMeters(1, PAGE_SIZE)],

    onSuccess: () => {
      toast({
        title: "Success",
        description: "Job card created successfully",
        variant: "default",
      });

      queryClient.refetchQueries({
        queryKey: ["mx", "allocationHistory"],
      });

      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.mx.device.detail(deviceId),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

export const useGetMeterModel = (
  meterType: string,
  meterManufacturer: string,
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.meterModel(meterType, meterManufacturer),
    () =>
      mxDeviceManagementApi.getMeterModel(
        remoteUtilityId,
        meterType,
        meterManufacturer,
      ),
    {
      enabled: !!remoteUtilityId && !!meterType && !!meterManufacturer,
    },
  );
};
export const useMeterManufacturers = (params: {
  remote_utility_id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.device.meterManufacturers(params),
    () => mxDeviceManagementApi.getMeterManufacturers(params),
  );
};

export const useGetManufacturers = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.manufacturer.manufacturers(),
    () => mxDeviceManagementApi.getManufacturers(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useMeterTypeCount = (remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meterTypeCount(),
    () => mxDeviceManagementApi.getMeterTypeCount(remoteUtilityId),
    { staleTime: 0, enabled: !!remoteUtilityId }, // 30 minutes
  );
};

export const useGetUsedMeters = (queryParams?: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.mx.usedMetersList(), queryParams],
    () => mxDeviceManagementApi.getUsedMeters(queryParams),
  );
};

export const useUsedMetersExport = (queryParams?: Record<string, any>) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      // Remove page and limit from export params to get all data
      const exportParams = { ...queryParams };
      delete exportParams.page;
      delete exportParams.limit;

      const blob = await mxDeviceManagementApi.exportUsedMeters(exportParams);

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export used meters: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [queryParams, isExporting, toast]);

  return { exportCSV, isExporting };
};

export const useReasonChoices = () => {
  return useSmartQuery(QueryKeyFactory.module.mx.reasonChoices(), () =>
    mxDeviceManagementApi.getReasonChoices(),
  );
};

export const useDisposeMeters = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return mxDeviceManagementApi.disposeMeters(payload);
    },
    {
      onError: (error: any) => {
        toast({ title: "Error While Disposing the Meter" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeyFactory.module.mx.usedMetersList()],
        });
        toast({ title: "Meters Disposed Successfully" });
      },
    },
  );
};

export const useGetMeterType = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.meterType(),
    () => mxDeviceManagementApi.getMeterType(),
  );
};

export const useUploadReading = (id: any) => {
  return useSmartMutation((payload: any) =>
    meterApi.uploadReading(payload, id),
  );
};

export const useAssignConsumer = () => {
  return useSmartMutation((payload: any) => meterApi.assignConsumer(payload), {
    onSuccess: () => {
      toast({
        title: "Consumer assigned successfully!",
        description: "The consumer was assigned to the selected meter.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to assign consumer",
        variant: "destructive",
        description:
          error?.response?.data?.message ||
          "Something went wrong while assigning the consumer.",
      });
    },
  });
};

export const useRoute = (premise: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.deviceManagement.route(premise),
    () => meterApi.getRoute(premise),
  );
};

export const useConsumers = (
  page: any,
  limit: any,
  status: any,
  searchData?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.deviceManagement.consumer({
      page,
      limit,
      status,
      searchData,
    }),
    () => meterApi.getConsumers(page, limit, status, searchData),
  );
};

export const usePriority = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.deviceManagement.priority(),
    () => meterApi.getPriority(),
  );
};

export const usePremise = (
  page: any,
  limit: any,
  search_data?: string,
  exclude_code_name?: any,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.deviceManagement.premise({
      page,
      limit,
      search_data,
      exclude_code_name,
    }),
    () => meterApi.getPremise(page, limit, search_data, exclude_code_name),
  );
};

export const useSubMeterSummary = ({
  meterId,
  remoteUtilityId,
}: {
  meterId: number;
  remoteUtilityId: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.subMeterSummary(meterId, remoteUtilityId),
    () => mxDeviceManagementApi.getSubMeterSummary(meterId, remoteUtilityId),
    {
      enabled: !!meterId && !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useSubMeterList = ({
  meterId,
  remoteUtilityId,
  page,
  limit,
  search,
}: {
  meterId: number;
  remoteUtilityId: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.subMeterList(
      meterId,
      remoteUtilityId,
      page,
      limit,
      search,
    ),
    () =>
      mxDeviceManagementApi.getSubMeterList({
        meterId,
        remoteUtilityId,
        page,
        limit,
        search,
      }),
    {
      enabled: !!meterId && !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useAddSubMeter = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: {
      master_meter_id: number;
      sub_meter_ids: number[];
      remote_utility_id: number;
    }) => mxDeviceManagementApi.addSubMeter(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Sub-meters linked successfully",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "submeter-list"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "submeter-summary"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.response?.data?.message || "Failed to link sub-meters",
          variant: "destructive",
        });
      },
    },
  );
};
